import React from "react";
import { Container, Row, Col } from "react-bootstrap";
import { motion } from "framer-motion";

// Animation variants
const sectionVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.7,
      ease: "easeOut",
    },
  },
};

const cardsWrapperVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.18,
    },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 35 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut",
    },
  },
};

const About = ({ data }) => {
  return (
    <motion.section
      id="about"
      style={{
        paddingTop: "90px",
        paddingBottom: "40px",
        background: "linear-gradient(180deg, #f9fdfd, #e6f7f7)",
      }}
      variants={sectionVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.3 }}
    >
      <Container style={{ maxWidth: "1100px" }}>
        {/* TITLE + INTRO */}
        <Row className="mb-3">
          <Col>
            <motion.h1
              style={{
                fontSize: "clamp(2.3rem, 4vw, 2.9rem)",
                fontWeight: "800",
                color: "#054E48",
                marginBottom: "14px",
                textAlign: "center",
              }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.5 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
            >
              What is Sari-sari Web Development?
            </motion.h1>

            <motion.p
              style={{
                fontSize: "clamp(1.1rem, 2.3vw, 1.35rem)",
                lineHeight: "2rem",
                color: "rgba(0,0,0,0.75)",
                textAlign: "center",
                maxWidth: "840px",
                margin: "0 auto",
              }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.5 }}
              transition={{ duration: 0.6, ease: "easeOut", delay: 0.1 }}
            >
              {data?.description ||
                "Sari-sari Web Development is a flexible, mix-and-match approach to building websites—just like a sari-sari store where you get only what you need. We deliver practical, modern, and affordable solutions for small businesses, freelancers, and growing brands."}
            </motion.p>
          </Col>
        </Row>

        {/* CARDS */}
        <motion.div
          variants={cardsWrapperVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          <Row className="mt-3 gy-4">
            {/* LEFT CARD */}
            <Col xs={12} md={6}>
              <motion.div
                variants={cardVariants}
                whileHover={{
                  y: -6,
                  boxShadow: "0 14px 30px rgba(0,0,0,0.15)",
                }}
                transition={{ type: "spring", stiffness: 220, damping: 18 }}
                style={{
                  background: "rgba(21, 207, 192, 0.12)",
                  borderRadius: "18px",
                  padding: "22px 24px",
                  border: "1px solid rgba(5, 78, 72, 0.25)",
                  boxShadow: "0 6px 18px rgba(0,0,0,0.06)",
                  height: "100%",
                  cursor: "default",
                }}
              >
                <h2
                  style={{
                    fontSize: "clamp(1.45rem, 3vw, 2rem)",
                    fontWeight: "700",
                    color: "#054E48",
                    marginBottom: "12px",
                  }}
                >
                  What we do
                </h2>

                <p
                  style={{
                    fontSize: "clamp(1.05rem, 2vw, 1.2rem)",
                    lineHeight: "1.9rem",
                    color: "rgba(0,0,0,0.75)",
                    marginBottom: "10px",
                  }}
                >
                  {data?.whatWeDo ||
                    "We build clear, modern, and user-friendly websites using React and Bootstrap. From landing pages to multi-step forms, we focus on simple, fast, and effective digital experiences."}
                </p>

                <ul
                  style={{
                    fontSize: "clamp(1rem, 1.9vw, 1.15rem)",
                    lineHeight: "1.9rem",
                    color: "rgba(0,0,0,0.85)",
                    paddingLeft: "1.2rem",
                    marginBottom: 0,
                  }}
                >
                  <li>Fast-loading landing pages</li>
                  <li>Simple multi-step onboarding forms</li>
                  <li>Modern mobile-first layouts</li>
                  <li>Lightweight React + Bootstrap builds</li>
                </ul>
              </motion.div>
            </Col>

            {/* RIGHT CARD */}
            <Col xs={12} md={6}>
              <motion.div
                variants={cardVariants}
                whileHover={{
                  y: -6,
                  boxShadow: "0 14px 30px rgba(0,0,0,0.15)",
                }}
                transition={{ type: "spring", stiffness: 220, damping: 18 }}
                style={{
                  background: "#ffffff",
                  borderRadius: "18px",
                  padding: "22px 24px",
                  border: "1px solid rgba(0,0,0,0.06)",
                  boxShadow: "0 6px 18px rgba(0,0,0,0.06)",
                  height: "100%",
                  cursor: "default",
                }}
              >
                <h2
                  style={{
                    fontSize: "clamp(1.45rem, 3vw, 2rem)",
                    fontWeight: "700",
                    color: "#054E48",
                    marginBottom: "12px",
                  }}
                >
                  Our approach
                </h2>

                <p
                  style={{
                    fontSize: "clamp(1.05rem, 2vw, 1.2rem)",
                    lineHeight: "1.9rem",
                    color: "rgba(0,0,0,0.75)",
                    marginBottom: "10px",
                  }}
                >
                  {data?.approach ||
                    "We start with your goal, then design only what’s needed to achieve it—nothing complicated, nothing unnecessary. Clean visuals, fast loading, and clear messaging."}
                </p>

                <p
                  style={{
                    fontSize: "clamp(1.05rem, 2vw, 1.2rem)",
                    lineHeight: "1.9rem",
                    color: "rgba(0,0,0,0.75)",
                    marginBottom: 0,
                  }}
                >
                  {data?.approach2 ||
                    "We make sure your website looks good on all devices and feels intuitive for your visitors, helping them take action effortlessly."}
                </p>
              </motion.div>
            </Col>
          </Row>
        </motion.div>
      </Container>
    </motion.section>
  );
};

export default About;
